import { sub } from 'date-fns';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { Platform } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import {
    ArrowUpRightIcon,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CalendarSync,
    CircleQuestionMarkIcon,
    CreditCardIcon,
    EyeIcon,
    EyeOffIcon,
    FullScreenSpinner,
    Icon,
    isNonMobileWeb,
    LockIcon,
    MailIcon,
    NavigationRow,
    Notice,
    PenSquareIcon,
    Row,
    RowList,
    SCREEN_CONTAINER_HORIZONTAL_PADDING,
    ScreenContainer,
    SettingsIcon,
    Sheet,
    Switch,
    TextRow,
    TileButton,
    UnlockIcon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../common/hooks/useSendEmailToSupport';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    canRequestCard,
    centsToFormattedDollars,
    formattedDollarsToCents,
    getLastFourAccountNumber,
    isCardLocked,
    isCardSuspended,
} from '../../../common/utils/bankingUtils';
import {
    accountHasNonTerminatedCardsFromOtherHolder,
    getCardPaymentStatus,
    getNonTerminatedCardsForUser,
    invertNormalizedValue,
} from '../../../common/utils/cardUtils';
import {
    format,
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
} from '../../../common/utils/dateUtils';
import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { captureException, captureMessage } from '../../../logger/Logger';
import useUpdateCreditCard from '../../hooks/card/useUpdateCreditCard';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import useUpdateAutoPay from '../../hooks/useUpdateAutoPay';
import { preselectedAccountForCardAtom } from '../../state/SpacesAtoms';
import { type BankAccountDisplayDetail } from '../../types/BankAccount';
import {
    type Card,
    CardForm,
    CardPaymentStatus,
    CardStatus,
    UpdateReason,
    UpdateStatus,
} from '../../types/Card';
import { getBankingDisclosures } from '../Disclosures';
import TransactionTable from '../spaces/transactions/transactionTable/TransactionTable';
import { AddToAppleWalletButton } from './AddToAppleWalletButton';
import { CardCarousel } from './CardCarousel';
import { AutoPayOverviewTab } from './EnableAutoPay';

const ManageCard = () => {
    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    const toast = useToastController();
    const sendEmailToSupport = useSendEmailToSupport();
    const user = useAuthedUser();
    const setPreselectedAccountForCard = useSetAtom(
        preselectedAccountForCardAtom,
    );

    const [showCardDetails, setShowCardDetails] = useState(false);
    const isFlipped = useSharedValue(false);

    const [isAutoPaySheetOpen, setIsAutoPaySheetOpen] = useState(false);
    const [isAutoPaySettingsSheetOpen, setIsAutoPaySettingsSheetOpen] =
        useState(false);
    const [isAutoPayConfirmSheetOpen, setIsAutoPayConfirmSheetOpen] =
        useState(false);
    const [isCardBalanceSheetOpen, setIsCardBalanceSheetOpen] = useState(false);
    const [isNoPaymentSheetOpen, setNoPaymentSheetOpen] = useState(false);
    const [isManageCardSheetOpen, setIsManageCardSheetOpen] = useState(false);
    const [activeCardIndex, setActiveCardIndex] = useState(0);

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    const { canRequestPhysicalCard } = useGetAllBankAccountDetails(false);

    const { mutateAsync: updateAutoPay, isPending: isUpdateAutoPayPending } =
        useUpdateAutoPay();
    const { refetchAccountDetails, isLoading: isLoadingAccountDetails } =
        useGetAllBankAccountDetails(false);

    if (syncteraAccountId == null) {
        captureMessage('Space not found for manage card page');
        return <Redirect href="/home/banking" />;
    }

    if (account == null || isUpdateAutoPayPending || isLoadingAccountDetails) {
        return <FullScreenSpinner />;
    }

    const {
        cards,
        cardBalance,
        daysPastDue,
        balanceDue,
        autoPayEnabled,
        autoPaymentDate,
        paymentDueDate,
    } = account;

    const cardPaymentStatus = getCardPaymentStatus({
        autoPayEnabled,
        paymentDueDate,
        balanceDue,
        daysPastDue,
    });

    const cardBalanceNormalized = invertNormalizedValue(cardBalance);
    const filteredCards = getNonTerminatedCardsForUser(
        user.syncteraPersonId,
        cards as Card[],
    );
    const activeCard = filteredCards[activeCardIndex];
    const canIssueCard = canRequestCard(user.syncteraPersonId, account);
    const requestCardText = canRequestPhysicalCard
        ? 'Add a Fruitful Card'
        : 'Request a virtual card';
    const manageCardText =
        activeCard == null && canIssueCard ? requestCardText : 'Manage card';

    const balanceDueCents = formattedDollarsToCents(balanceDue ?? '$0.00');
    const thisMonthsSpendingFormatted = centsToFormattedDollars(
        formattedDollarsToCents(cardBalanceNormalized) - balanceDueCents,
    );
    const today = new Date();
    const currentMonthName = format(today, 'MMMM');
    const lastMonthName = format(sub(today, { months: 1 }), 'MMMM');
    const isPostTwentySecond = today.getDate() > 22;
    const isPreTwentySecond = !isPostTwentySecond;
    const autoPaySubtitle =
        autoPaymentDate != null
            ? `AutoPay on 5 PM ET ${formatWithoutTimezone(autoPaymentDate, SHORT_READABLE_DATE_FORMAT)}`
            : undefined;

    const balanceSubtitle = autoPayEnabled
        ? autoPaySubtitle
        : paymentDueDate != null
          ? `Due 5 PM ET ${formatWithoutTimezone(paymentDueDate, SHORT_READABLE_DATE_FORMAT)}`
          : undefined;

    const isPastDue = daysPastDue != null && daysPastDue > 0;
    const hasBalanceDue = balanceDueCents > 0;

    const handleUpdateAutoPay = async (autoPayEnabled: boolean) => {
        try {
            await updateAutoPay({
                urlParams: {
                    syncteraAccountId:
                        account.chargeSecuredSyncteraAccountId as string,
                },
                body: {
                    autoPayEnabled,
                    disclosures: getBankingDisclosures({
                        enableAutoPay: true,
                        policyTypes: [],
                    }),
                },
            });
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        }
        void refetchAccountDetails();
    };

    const onPressMakePayment = () => {
        if (autoPayEnabled === true) {
            setNoPaymentSheetOpen(true);
        } else {
            router.navigate({
                pathname: '/banking/card/make-payment',
                params: {
                    syncteraAccountId,
                },
            });
        }
    };

    const reportAnIssue = () => {
        void sendEmailToSupport({
            subject: 'Manage card report an issue',
        });
    };

    const onRequestCard = () => {
        setPreselectedAccountForCard(account);
        router.navigate({
            pathname: '/banking/onboarding/add-card',
            ...(accountHasNonTerminatedCardsFromOtherHolder(
                user.syncteraPersonId,
                account,
            )
                ? {
                      params: {
                          isJointInvitation: 'true',
                      },
                  }
                : {}),
        });
    };

    return (
        <ScreenContainer
            title={`${account.nickname} Card`}
            hasBackButton={true}
        >
            <YStack gap={16}>
                {daysPastDue != null && daysPastDue > 0 && (
                    <Notice
                        variant="danger"
                        title="Payment overdue"
                        description="Reactivate your card by paying the overdue balance today."
                        action={{
                            text: 'Make a payment',
                            onPress: onPressMakePayment,
                        }}
                    />
                )}

                {!isEmpty(cards) && (
                    <View
                        marginHorizontal={
                            isNonMobileWeb
                                ? 0
                                : -SCREEN_CONTAINER_HORIZONTAL_PADDING
                        }
                    >
                        <CardCarousel
                            syncteraAccountId={syncteraAccountId}
                            cards={cards as Card[]}
                            isFlipped={isFlipped}
                            showDetailsHint={showCardDetails}
                            onIndexChange={(index) => {
                                setActiveCardIndex(index);
                                isFlipped.value = false;
                                setShowCardDetails(false);
                            }}
                        />
                    </View>
                )}

                {activeCard?.form === CardForm.PHYSICAL &&
                    activeCard.cardStatus === CardStatus.UNACTIVATED && (
                        <YStack
                            backgroundColor="$gray200"
                            padding={16}
                            gap={16}
                            borderRadius={24}
                        >
                            <BodyLargeEmphasized>
                                Your card is on its way
                            </BodyLargeEmphasized>

                            <BodyMediumRegular>
                                The physical card you requested should arrive in
                                the next 5-7 business days.
                            </BodyMediumRegular>

                            <Button
                                text="Activate card"
                                size={ButtonSize.LARGE}
                                variant={ButtonVariant.PRIMARY}
                                onPress={() => {
                                    router.navigate({
                                        pathname: '/banking/card/activate-card',
                                        params: {
                                            syncteraAccountId:
                                                activeCard.syncteraAccountId,
                                            cardId: activeCard.id,
                                        },
                                    });
                                }}
                            />
                        </YStack>
                    )}

                <XStack gap={16} height={80}>
                    <TileButton
                        icon={showCardDetails ? EyeOffIcon : EyeIcon}
                        text={
                            showCardDetails
                                ? 'Hide card details'
                                : 'Show card details'
                        }
                        onPress={() => {
                            const next = !showCardDetails;
                            setShowCardDetails(next);
                            isFlipped.value = next;
                        }}
                        tileProps={{
                            disabled:
                                activeCard?.cardStatus !== CardStatus.ACTIVE,
                            borderRadius: 24,
                            paddingVertical: 12,
                            paddingHorizontal: 16,
                        }}
                    />
                    <TileButton
                        icon={CreditCardIcon}
                        tileProps={{
                            borderRadius: 24,
                            paddingVertical: 12,
                            paddingHorizontal: 16,
                        }}
                        text={manageCardText}
                        onPress={() => {
                            if (activeCard == null && canIssueCard) {
                                onRequestCard();
                                return;
                            }

                            setIsManageCardSheetOpen(true);
                        }}
                    />
                </XStack>

                <RowList
                    rowHeaderProps={{
                        title: 'Your card spend',
                        subtitle: autoPayEnabled
                            ? 'AutoPay is on'
                            : 'AutoPay is off. Turn it on and your reserved funds will cover your balance automatically on the 22nd of each month.',
                        primaryButton: {
                            icon: SettingsIcon,
                            onPress: () => {
                                setIsAutoPaySettingsSheetOpen(true);
                            },
                        },
                    }}
                >
                    <TextRow
                        title={`${currentMonthName} balance`}
                        subtitle={isPostTwentySecond ? balanceSubtitle : ''}
                        secondaryText={thisMonthsSpendingFormatted}
                    />

                    {/* We only show the second row if
                        (1) we have a balance and
                        (2) we are pre-22nd of the month or
                        (3) we are past due
                    */}
                    {hasBalanceDue && (isPreTwentySecond || isPastDue) && (
                        <TextRow
                            title={`${lastMonthName} balance`}
                            subtitle={balanceSubtitle}
                            secondaryText={balanceDue ?? ''}
                            {...(isPastDue && {
                                titleProps: { color: '$red1000' },
                                subtitleProps: { color: '$red1000' },
                                secondaryTextProps: { color: '$red1000' },
                            })}
                        />
                    )}

                    <XStack padding={16}>
                        <Button
                            variant={
                                isPastDue || !autoPayEnabled
                                    ? ButtonVariant.PRIMARY
                                    : ButtonVariant.SECONDARY
                            }
                            size={ButtonSize.LARGE}
                            text="Make a payment"
                            onPress={onPressMakePayment}
                        />
                    </XStack>
                </RowList>

                <TransactionTable
                    bankAccountId={account.bankAccountId}
                    syncteraAccountId={
                        account?.chargeSecuredSyncteraAccountId ?? ''
                    }
                    isSingleTableView
                    isPaymentTransactionTable
                />

                <RowList>
                    <NavigationRow
                        leftIcon={CircleQuestionMarkIcon}
                        title="Fruitful Card FAQ"
                        subtitle="More about the Fruitful Card"
                        onPress={() => {
                            router.navigate('/settings/faq');
                        }}
                    />
                    <Row
                        leftIcon={MailIcon}
                        title="Report a problem"
                        subtitle="Send us an email to report"
                        rightContent={<Icon icon={ArrowUpRightIcon} />}
                        onPress={reportAnIssue}
                    />
                    {!IS_PRODUCTION && (
                        <NavigationRow
                            title="Simulate transaction"
                            onPress={() => {
                                router.navigate({
                                    pathname:
                                        '/banking/card/simulate-transaction',
                                    params: {
                                        cardId: activeCard?.id,
                                    },
                                });
                            }}
                        />
                    )}
                </RowList>
            </YStack>

            <ManageCardSheet
                isOpen={isManageCardSheetOpen}
                onClose={() => {
                    setIsManageCardSheetOpen(false);
                }}
                card={activeCard}
                syncteraAccountId={syncteraAccountId}
                chargeSecuredSyncteraAccountId={
                    account.chargeSecuredSyncteraAccountId
                }
                canRequestPhysicalCard={canRequestPhysicalCard}
                canIssueCard={canIssueCard}
                onRequestCard={onRequestCard}
                onReportIssue={reportAnIssue}
            />

            <AutoPaySettingsSheet
                isOpen={isAutoPaySettingsSheetOpen}
                autoPayEnabled={autoPayEnabled}
                autoPaymentDate={autoPaymentDate}
                paymentDueDate={paymentDueDate}
                onClose={() => {
                    setIsAutoPaySettingsSheetOpen(false);
                }}
                onToggle={(checked) => {
                    if (checked) {
                        setIsAutoPaySettingsSheetOpen(false);
                        setIsAutoPayConfirmSheetOpen(true);
                    } else {
                        setIsAutoPaySettingsSheetOpen(false);
                        setIsAutoPaySheetOpen(true);
                    }
                }}
            />

            <AutoPaySheet
                isOpen={isAutoPaySheetOpen}
                onPressAutoPayOff={async () => {
                    setIsAutoPaySheetOpen(false);
                    await handleUpdateAutoPay(false);
                }}
                onClose={() => {
                    setIsAutoPaySheetOpen(false);
                }}
            />

            <CardBalanceSheet
                isOpen={isCardBalanceSheetOpen}
                onClose={() => {
                    setIsCardBalanceSheetOpen(false);
                }}
                balanceDue={balanceDue}
                lastMonth={lastMonthName}
                cardBalance={cardBalanceNormalized}
                paymentDueDate={paymentDueDate}
                autoPayEnabled={autoPayEnabled ?? false}
                thisMonthsSpending={thisMonthsSpendingFormatted}
                cardPaymentStatus={cardPaymentStatus}
            />

            <NoPaymentSheet
                isOpen={isNoPaymentSheetOpen}
                autoPaymentDate={autoPaymentDate}
                onClose={() => {
                    setNoPaymentSheetOpen(false);
                }}
                onPressMakePayment={() => {
                    setNoPaymentSheetOpen(false);
                    router.navigate({
                        pathname: '/banking/card/make-payment',
                        params: {
                            syncteraAccountId,
                        },
                    });
                }}
            />

            <AutopayConfirmSheet
                isOpen={isAutoPayConfirmSheetOpen}
                account={account}
                cards={filteredCards}
                onClose={() => {
                    setIsAutoPayConfirmSheetOpen(false);
                }}
                onConfirm={async () => {
                    setIsAutoPayConfirmSheetOpen(false);
                    await handleUpdateAutoPay(true);
                }}
            />
        </ScreenContainer>
    );
};

export default ManageCard;

interface ManageCardSheetProps {
    isOpen: boolean;
    onClose: () => void;
    card?: Card;
    syncteraAccountId: string;
    chargeSecuredSyncteraAccountId?: string | null;
    canRequestPhysicalCard: boolean;
    canIssueCard: boolean;
    onRequestCard: () => void;
    onReportIssue: () => Promise<void>;
}

const ManageCardSheet = ({
    isOpen,
    onClose,
    card,
    syncteraAccountId,
    chargeSecuredSyncteraAccountId,
    canRequestPhysicalCard,
    canIssueCard,
    onRequestCard,
}: ManageCardSheetProps) => {
    const toast = useToastController();
    const { mutateAsync: updateCreditCard } = useUpdateCreditCard();
    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    const unactivated = card?.cardStatus === CardStatus.UNACTIVATED;
    const pending = card?.cardStatus === CardStatus.PENDING;
    const locked =
        card != null &&
        isCardLocked({
            cardStatus: card.cardStatus,
            userCardStatusReason: card.userCardStatusReason,
        });
    const suspended =
        card != null &&
        isCardSuspended({
            cardStatus: card.cardStatus,
            userCardStatusReason: card.userCardStatusReason,
        });
    const showAppleWalletButton =
        card?.cardStatus === CardStatus.ACTIVE && Platform.OS === 'ios';
    const canReplaceCard =
        card != null &&
        card.cardStatus !== CardStatus.UNACTIVATED &&
        card.cardStatus !== CardStatus.PENDING &&
        !suspended &&
        card.cardStatus !== CardStatus.TERMINATED &&
        card.form !== CardForm.VIRTUAL &&
        card.reissuedToId == null;
    const requestCardText = canRequestPhysicalCard
        ? 'Add a Fruitful Card'
        : 'Request a virtual card';

    const onLockUnlock = async () => {
        if (card == null) return;
        if (locked) {
            try {
                await updateCreditCard({
                    urlParams: { cardId: card.id },
                    body: {
                        cardStatus: UpdateStatus.ACTIVE,
                        reason: UpdateReason.REQ,
                    },
                });
                await refetchAccountDetails();
                onClose();
                toast.show('Card unlocked', { duration: 2000 });
            } catch (e) {
                captureException(e);
                toast.show('Something went wrong.');
            }
        } else {
            onClose();
            router.navigate({
                pathname: '/banking/card/lock-card',
                params: { syncteraAccountId, cardId: card.id },
            });
        }
    };

    const onChangePin = () => {
        if (card == null) return;
        onClose();
        router.navigate({
            pathname: '/verify',
            params: {
                chargeSecuredSyncteraAccountId,
                cardId: card.id,
                callback: '/banking/card/change-pin',
            },
        });
    };

    const onReplaceCard = () => {
        if (card == null) return;
        onClose();
        router.navigate({
            pathname: '/banking/card/replace-card',
            params: { syncteraAccountId, cardId: card.id },
        });
    };

    const onOrderPhysicalCard = () => {
        onClose();
        router.navigate({
            pathname: '/banking/card/request-physical-card',
            params: { syncteraAccountId },
        });
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={
                card == null
                    ? 'Manage card'
                    : `Manage ${card.form === CardForm.VIRTUAL ? 'virtual' : 'physical'} card`
            }
            showCloseButton={false}
        >
            {card == null && canIssueCard && (
                <RowList>
                    <NavigationRow
                        leftIcon={CreditCardIcon}
                        title={requestCardText}
                        subtitle="Create a card for this account"
                        onPress={() => {
                            onClose();
                            onRequestCard();
                        }}
                    />
                </RowList>
            )}
            {card != null && (
                <RowList>
                    <NavigationRow
                        leftIcon={locked ? UnlockIcon : LockIcon}
                        title={locked ? 'Unlock card' : 'Lock card'}
                        subtitle={
                            locked
                                ? 'Re-enable your card'
                                : 'Temporarily disable your card'
                        }
                        onPress={() => {
                            void onLockUnlock();
                        }}
                        disabled={unactivated || pending || suspended}
                    />
                    {card.form === CardForm.PHYSICAL && (
                        <NavigationRow
                            leftIcon={PenSquareIcon}
                            title="Change PIN"
                            subtitle="Change your card PIN"
                            onPress={onChangePin}
                            disabled={card.cardStatus !== CardStatus.ACTIVE}
                        />
                    )}
                    {canReplaceCard && (
                        <NavigationRow
                            leftIcon={CreditCardIcon}
                            title="Request a replacement card"
                            subtitle="Replace your stolen or lost card"
                            onPress={onReplaceCard}
                        />
                    )}
                    {card.form === CardForm.VIRTUAL && (
                        <NavigationRow
                            leftIcon={CreditCardIcon}
                            title="Order a physical card"
                            subtitle={
                                canRequestPhysicalCard
                                    ? 'Get a physical card mailed to you'
                                    : 'Each member is eligible for one physical card'
                            }
                            onPress={onOrderPhysicalCard}
                            disabled={!canRequestPhysicalCard}
                        />
                    )}
                    {!showAppleWalletButton && (
                        <NavigationRow
                            leftIcon={CreditCardIcon}
                            title="Add to Apple Wallet"
                            onPress={() => {
                                onClose();
                                router.navigate('/banking/card/apple-wallet');
                            }}
                        />
                    )}
                    <NavigationRow
                        leftIcon={CreditCardIcon}
                        title="Add to Google Pay"
                        onPress={() => {
                            onClose();
                            router.navigate('/banking/card/google-pay');
                        }}
                    />
                </RowList>
            )}
            {showAppleWalletButton && card != null && (
                <AddToAppleWalletButton
                    card={card}
                    onProgress={() => {
                        // Show loading indicator
                        toast.show('Adding to Apple Wallet', {
                            duration: 2000,
                        });
                    }}
                    onSuccess={() => {
                        toast.show('Card added to Apple Wallet', {
                            duration: 2000,
                        });
                    }}
                    onError={() => {
                        toast.show('Failed to add card to Apple Wallet', {
                            duration: 2000,
                        });
                    }}
                />
            )}
            <Button
                text="Done"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                onPress={onClose}
            />
        </Sheet>
    );
};

interface AutoPaySettingsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    autoPayEnabled?: boolean | null;
    autoPaymentDate?: string | null;
    paymentDueDate?: string | null;
    onToggle: (checked: boolean) => void;
}

const AutoPaySettingsSheet = ({
    isOpen,
    onClose,
    autoPayEnabled,
    autoPaymentDate,
    paymentDueDate,
    onToggle,
}: AutoPaySettingsSheetProps) => {
    const getSubtitle = () => {
        if (autoPayEnabled && autoPaymentDate != null) {
            return `Next payment due 5 PM ET ${formatWithoutTimezone(autoPaymentDate, SHORT_READABLE_DATE_FORMAT)}`;
        }
        if (paymentDueDate != null) {
            return `Next payment due ${formatWithoutTimezone(paymentDueDate, SHORT_READABLE_DATE_FORMAT)}`;
        }
        return undefined;
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="AutoPay settings"
            showCloseButton={false}
        >
            <BodySmallRegular width="100%" textAlign="center" color="$gray1000">
                Your card will be paid off automatically using reserved funds on
                the 22nd of every month.
            </BodySmallRegular>

            <RowList>
                <Row
                    leftIcon={CalendarSync}
                    title={`Card AutoPay is ${autoPayEnabled ? 'on' : 'off'}`}
                    subtitle={getSubtitle()}
                    rightContent={
                        <Switch
                            isChecked={!!autoPayEnabled}
                            onCheckedChange={onToggle}
                        />
                    }
                />
            </RowList>
            <Button
                text="Done"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                onPress={onClose}
            />
        </Sheet>
    );
};

interface AutoPaySheetProps {
    isOpen: boolean;
    onClose: () => void;
    onPressAutoPayOff: () => void;
}

const AutoPaySheet = ({
    isOpen,
    onClose,
    onPressAutoPayOff,
}: AutoPaySheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Are you sure?"
            showCloseButton
        >
            <YStack width="100%" maxWidth={400} alignSelf="center">
                <BodyLargeEmphasized>
                    Turning off AutoPay means you&apos;ll have to make manual
                    payments, which we do not recommend to get the best use out
                    of your card.
                </BodyLargeEmphasized>
            </YStack>
            <YStack marginTop={20} gap={12}>
                <Button
                    text="Leave AutoPay on"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    onPress={onClose}
                />
                <Button
                    text="Turn AutoPay off"
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    tone="danger"
                    onPress={onPressAutoPayOff}
                />
            </YStack>
        </Sheet>
    );
};

const AutopayConfirmSheet = ({
    isOpen,
    account,
    cards,
    onConfirm,
    onClose,
}: {
    isOpen: boolean;
    account: BankAccountDisplayDetail;
    cards: Card[];
    onConfirm: () => void;
    onClose: () => void;
}) => {
    return (
        <Sheet isOpen={isOpen} showCloseButton={true} onClose={onClose}>
            <AutoPayOverviewTab
                card={`${account.nickname} Card (${cards
                    .map((card) => `*${card.lastFour}`)
                    ?.join(', ')})`}
                from={`Reserved funds in ${account.nickname} (*${getLastFourAccountNumber(account.accountNumber)})`}
                onPress={onConfirm}
            />
        </Sheet>
    );
};

interface CardBalanceSheetProps {
    isOpen: boolean;
    onClose: () => void;
    cardBalance: string;
    balanceDue?: string | null;
    thisMonthsSpending: string;
    autoPayEnabled: boolean;
    paymentDueDate?: string | null;
    cardPaymentStatus: CardPaymentStatus;
    lastMonth: string;
}

const CardBalanceSheet = ({
    isOpen,
    onClose,
    cardBalance,
    balanceDue,
    thisMonthsSpending,
    autoPayEnabled,
    paymentDueDate,
    cardPaymentStatus,
    lastMonth,
}: CardBalanceSheetProps) => {
    const getOutstandingBalanceExplanation = () => {
        if (cardPaymentStatus === CardPaymentStatus.OverdueManualPayment) {
            return 'Balance past due.';
        }
        if (autoPayEnabled) {
            return 'Your upcoming AutoPay will cover this.';
        }
        if (
            paymentDueDate != null &&
            balanceDue != null &&
            balanceDue !== '$0.00'
        ) {
            return `Next payment due ${formatWithoutTimezone(paymentDueDate, SHORT_READABLE_DATE_FORMAT)}.`;
        }
        return '';
    };

    const getOutstandingBalanceWarning = () => {
        if (cardPaymentStatus === CardPaymentStatus.UpcomingManualPayment) {
            return 'Due soon';
        }
        if (cardPaymentStatus === CardPaymentStatus.OverdueManualPayment) {
            return 'Overdue';
        }
        return undefined;
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Card balance"
            showCloseButton
        >
            <RowList>
                <TextRow
                    title="This month's outstanding balance"
                    subtitle="Posted transactions since last statement, minus any payments made."
                    secondaryText={thisMonthsSpending}
                    isPrivate
                />
                <TextRow
                    title={`Outstanding ${lastMonth} balance`}
                    subtitle={getOutstandingBalanceExplanation()}
                    secondaryText={balanceDue ?? '$0.00'}
                    secondarySubtext={getOutstandingBalanceWarning()}
                    secondarySubtextProps={{
                        color:
                            cardPaymentStatus ===
                            CardPaymentStatus.UpcomingManualPayment
                                ? '$orange1100'
                                : cardPaymentStatus ===
                                    CardPaymentStatus.OverdueManualPayment
                                  ? '$red1100'
                                  : undefined,
                    }}
                    isPrivate
                />
                <TextRow
                    title="Card balance"
                    subtitle="Your card balance includes any purchases made in the current statement period, plus any balance due from previous months."
                    secondaryText={cardBalance}
                    isPrivate
                />
            </RowList>
        </Sheet>
    );
};

interface NoPaymentSheetProps {
    isOpen: boolean;
    onClose: () => void;
    onPressMakePayment: () => void;
    autoPaymentDate?: string | null;
}

const NoPaymentSheet = ({
    isOpen,
    onClose,
    onPressMakePayment,
    autoPaymentDate,
}: NoPaymentSheetProps) => {
    const formattedAutoPaymentDate =
        autoPaymentDate != null
            ? formatWithoutTimezone(autoPaymentDate, SHORT_READABLE_DATE_FORMAT)
            : null;
    const upcomingAutoPaymentText =
        formattedAutoPaymentDate != null &&
        formattedAutoPaymentDate >=
            formatWithoutTimezone(new Date(), SHORT_READABLE_DATE_FORMAT)
            ? ` for ${formattedAutoPaymentDate} `
            : ' ';

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="No payment necessary!"
            showCloseButton
        >
            <YStack>
                <BodyLargeRegular marginVertical={20}>
                    Your next AutoPay is set
                    {upcomingAutoPaymentText} with funds already reserved for
                    payment.
                </BodyLargeRegular>

                <Button
                    text="Make a payment anyway"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.LARGE}
                    onPress={onPressMakePayment}
                />
            </YStack>
        </Sheet>
    );
};
